import { useState } from 'react';

interface IzakayaAreaProps {
  lang: 'ja' | 'en';
}

export const IzakayaArea: React.FC<IzakayaAreaProps> = ({ lang }) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', date: '', guests: '2', message: '' });

  const content = {
    ja: {
      badge: '📍 開催エリア',
      title: '荏原町・中延・戸越・武蔵小山',
      subtitle: '地元民が通う、昭和レトロな下町エリア',
      description: '観光客がほとんど来ない、本物の東京下町。昭和の雰囲気が残る商店街と、地元の常連さんで賑わう居酒屋が立ち並ぶエリアです。ここでしか味わえないリアルな東京の夜を体験しましょう。',
      areas: [
        {
          name: '荏原町',
          icon: 'ri-store-2-line',
          image: 'https://public.readdy.ai/ai/img_res/a1ba35b17c214907aec64daa98b9e852.jpg',
          tags: ['昭和レトロ', '地元密着', '穴場'],
          description: '昭和の面影が色濃く残る商店街。地元の常連さんが集まる小さな居酒屋が点在し、アットホームな雰囲気が魅力。'
        },
        {
          name: '中延',
          icon: 'ri-goblet-line',
          image: 'https://readdy.ai/api/search-image?query=Nakanobu%20Tokyo%20local%20izakaya%20street%20night%20scene%20warm%20lantern%20light%20small%20cozy%20japanese%20pub%20alley%20showa%20retro%20neighborhood%20authentic%20local%20bar&width=600&height=400&seq=area-nakanobu&orientation=landscape',
          tags: ['せんべろ', '下町情緒', '庶民的'],
          description: '「せんべろ」文化が根付く庶民的なエリア。安くて美味しい、地元民御用達の居酒屋が揃っています。'
        },
        {
          name: '戸越',
          icon: 'ri-map-pin-line',
          image: 'https://readdy.ai/api/search-image?query=Togoshi%20Tokyo%20famous%20shopping%20street%20evening%20izakaya%20local%20restaurants%20warm%20lights%20traditional%20japanese%20neighborhood%20vibrant%20street%20food%20culture%20authentic&width=600&height=400&seq=area-togoshi&orientation=landscape',
          tags: ['戸越銀座', '食文化', '活気'],
          description: '日本最長級の商店街「戸越銀座」が有名。食文化が豊かで、多彩な飲食店が軒を連ねる活気あるエリア。'
        },
        {
          name: '武蔵小山',
          icon: 'ri-building-line',
          image: 'https://readdy.ai/api/search-image?query=Musashi-Koyama%20Tokyo%20covered%20shopping%20arcade%20evening%20local%20izakaya%20warm%20atmosphere%20retro%20japanese%20shotengai%20neighborhood%20authentic%20local%20dining%20scene&width=600&height=400&seq=area-musashikoyama&orientation=landscape',
          tags: ['アーケード', '老舗', '人気エリア'],
          description: '全蓋式アーケード商店街として有名。老舗の居酒屋から新しいお店まで、幅広い選択肢が揃う人気エリア。'
        }
      ],
      mapTitle: '🗺️ ツアー開催エリアマップ',
      mapSubtitle: '東急池上線・大井町線沿線の下町エリアを巡ります',
      mapSpots: [
        { name: '荏原町駅', desc: '昭和レトロな商店街の起点' },
        { name: '中延駅', desc: 'せんべろ文化の聖地' },
        { name: '戸越銀座駅', desc: '日本最長級の商店街' },
        { name: '武蔵小山駅', desc: '全蓋アーケードの人気エリア' },
      ],
      access: {
        title: 'アクセス',
        description: '東急池上線・大井町線沿線。品川・渋谷・目黒から電車で約10〜20分。',
        icon: 'ri-train-line'
      },
      cta: 'このエリアで体験を予約する',
      bookingBlock: {
        badge: '🍺 このエリアで予約する',
        title: 'エリアを見たら、すぐ予約',
        subtitle: 'WhatsAppで即相談、またはフォームから予約リクエストを送れます',
        whatsappBtn: 'WhatsAppで今すぐ相談',
        orText: 'または',
        formTitle: '予約リクエストフォーム',
        fields: {
          name: 'お名前',
          email: 'メールアドレス',
          date: '希望日',
          guests: '参加人数',
          message: 'ご要望・ご質問（任意）',
          submit: '予約リクエストを送る',
        },
        guestOptions: ['1名', '2名', '3名', '4名', '5名', '6名'],
        successMsg: 'リクエストを受け付けました！24時間以内にご連絡します 🎉',
        note: '※ 確定はWhatsAppまたはメールにてご連絡します',
      },
    },
    en: {
      badge: '📍 Experience Area',
      title: 'Ebara-cho · Nakanobu · Togoshi · Musashi-Koyama',
      subtitle: 'Authentic Showa-era neighborhoods where locals actually drink',
      description: 'Far from the tourist crowds, these are real Tokyo neighborhoods. Retro shopping streets, izakayas packed with regulars, and an atmosphere you simply can\'t find in Shinjuku or Shibuya. This is the Tokyo that most visitors never see.',
      areas: [
        {
          name: 'Ebara-cho',
          icon: 'ri-store-2-line',
          image: 'https://readdy.ai/api/search-image?query=Ebara-cho%20Tokyo%20retro%20showa%20era%20shopping%20street%20narrow%20alley%20izakaya%20lanterns%20warm%20evening%20glow%20local%20neighborhood%20authentic%20japanese%20pub%20street%20scene%20vintage%20atmosphere&width=600&height=400&seq=area-ebara-en&orientation=landscape',
          tags: ['Showa Retro', 'Local Vibe', 'Hidden Gem'],
          description: 'A shopping street with strong Showa-era character. Small izakayas filled with regulars create a warm, homey atmosphere.'
        },
        {
          name: 'Nakanobu',
          icon: 'ri-goblet-line',
          image: 'https://readdy.ai/api/search-image?query=Nakanobu%20Tokyo%20local%20izakaya%20street%20night%20scene%20warm%20lantern%20light%20small%20cozy%20japanese%20pub%20alley%20showa%20retro%20neighborhood%20authentic%20local%20bar&width=600&height=400&seq=area-nakanobu-en&orientation=landscape',
          tags: ['Senbero Culture', 'Downtown Feel', 'Affordable'],
          description: 'Known for "senbero" culture—great drinks at great prices. A down-to-earth area beloved by locals.'
        },
        {
          name: 'Togoshi',
          icon: 'ri-map-pin-line',
          image: 'https://readdy.ai/api/search-image?query=Togoshi%20Tokyo%20famous%20shopping%20street%20evening%20izakaya%20local%20restaurants%20warm%20lights%20traditional%20japanese%20neighborhood%20vibrant%20street%20food%20culture%20authentic&width=600&height=400&seq=area-togoshi-en&orientation=landscape',
          tags: ['Togoshi Ginza', 'Food Culture', 'Lively'],
          description: 'Home to "Togoshi Ginza," one of Japan\'s longest shopping streets. Rich food culture with a wide variety of eateries.'
        },
        {
          name: 'Musashi-Koyama',
          icon: 'ri-building-line',
          image: 'https://readdy.ai/api/search-image?query=Musashi-Koyama%20Tokyo%20covered%20shopping%20arcade%20evening%20local%20izakaya%20warm%20atmosphere%20retro%20japanese%20shotengai%20neighborhood%20authentic%20local%20dining%20scene&width=600&height=400&seq=area-musashikoyama-en&orientation=landscape',
          tags: ['Covered Arcade', 'Long-established', 'Popular'],
          description: 'Famous for its fully covered shopping arcade. From long-established izakayas to newer spots, a popular area with something for everyone.'
        }
      ],
      mapTitle: '🗺️ Tour Area Map',
      mapSubtitle: 'We explore local neighborhoods along the Tokyu Ikegami & Oimachi Lines',
      mapSpots: [
        { name: 'Ebara-cho Sta.', desc: 'Starting point of the Showa retro street' },
        { name: 'Nakanobu Sta.', desc: 'Senbero culture hub' },
        { name: 'Togoshi Ginza Sta.', desc: 'Japan\'s longest shopping street' },
        { name: 'Musashi-Koyama Sta.', desc: 'Popular covered arcade area' },
      ],
      access: {
        title: 'Access',
        description: 'Along the Tokyu Ikegami & Oimachi Lines. About 10–20 min from Shinagawa, Shibuya, or Meguro.',
        icon: 'ri-train-line'
      },
      cta: 'Book an Experience in This Area',
      bookingBlock: {
        badge: '🍺 Book in This Area',
        title: 'Seen the Area? Book Now',
        subtitle: 'Chat on WhatsApp instantly, or send a booking request via the form below',
        whatsappBtn: 'Chat on WhatsApp Now',
        orText: 'or',
        formTitle: 'Booking Request Form',
        fields: {
          name: 'Your Name',
          email: 'Email Address',
          date: 'Preferred Date',
          guests: 'Number of Guests',
          message: 'Requests / Questions (optional)',
          submit: 'Send Booking Request',
        },
        guestOptions: ['1 guest', '2 guests', '3 guests', '4 guests', '5 guests', '6 guests'],
        successMsg: 'Request received! We\'ll get back to you within 24 hours 🎉',
        note: '* Confirmation will be sent via WhatsApp or email',
      },
    }
  };

  const t = content[lang];

  const handleWhatsAppClick = () => {
    const message = lang === 'ja'
      ? '荏原町・中延・戸越・武蔵小山エリアのローカル体験について相談したいです'
      : 'Hi! I\'d love to explore the local izakaya scene in Ebara-cho, Nakanobu, Togoshi, or Musashi-Koyama with a guide. Could you tell me more about the experience and how to book?';
    window.open(`https://wa.me/819092895522?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.append('name', formData.name);
    params.append('email', formData.email);
    params.append('date', formData.date);
    params.append('guests', formData.guests);
    params.append('message', formData.message);
    await fetch('https://readdy.ai/api/form/d6hs2ilpk1ctp7d9b8kg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });
    setFormSubmitted(true);
  };

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-amber-100 text-amber-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            {t.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-amber-600 font-medium mb-6">{t.subtitle}</p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t.description}
          </p>
        </div>

        {/* Area Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {t.areas.map((area, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
            >
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={area.image}
                  alt={area.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg">
                    <i className={`${area.icon} text-xl text-amber-600`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{area.name}</h3>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {area.tags.map((tag, i) => (
                    <span key={i} className="text-xs font-semibold bg-amber-50 text-amber-700 px-3 py-1 rounded-full border border-amber-200">
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed text-base">{area.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Google Map Section */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.mapTitle}</h3>
            <p className="text-gray-500 text-base">{t.mapSubtitle}</p>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-xl border border-amber-100">
            <iframe
              title="Tour Area Map"
              src="https://www.google.com/maps/embed?pb=!1m56!1m12!1m3!1d13003.45!2d139.7!3d35.605!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m41!3e2!4m5!1s0x60188538b3b3b3b3%3A0x0!2z6LC344Gu55S677yI5p2x5Lqs6YO95ZKM5Yy65Yy65Yy6!3m2!1d35.6052!2d139.7175!4m5!1s0x601885a0a0a0a0a0%3A0x0!2z5Lit5bGx!3m2!1d35.6080!2d139.7120!4m5!1s0x601885b0b0b0b0b0%3A0x0!2z5oi_6LaK!3m2!1d35.6010!2d139.7090!4m5!1s0x601885c0c0c0c0c0%3A0x0!2z5q2j6JGJ5bCP5bGx!3m2!1d35.6230!2d139.7060!5e0!3m2!1sja!2sjp!4v1700000000000!5m2!1sja!2sjp&q=荏原町+中延+戸越銀座+武蔵小山+東京&center=35.610,139.710&zoom=14"
              width="100%"
              height="480"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>

          {/* Spot Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {t.mapSpots.map((spot, i) => (
              <div key={i} className="flex items-start gap-3 bg-amber-50 rounded-xl p-4 border border-amber-100">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-400 rounded-full flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">{i + 1}</span>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{spot.name}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{spot.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== BOOKING CTA BLOCK ===== */}
        <div className="mb-12 bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl border border-amber-200 overflow-hidden shadow-lg">
          {/* Top Banner */}
          <div className="bg-amber-500 px-8 py-4 flex items-center justify-between flex-wrap gap-3">
            <span className="text-white font-bold text-lg tracking-wide">{t.bookingBlock.badge}</span>
            <div className="flex items-center gap-2 text-amber-100 text-sm">
              <i className="ri-time-line"></i>
              <span>{lang === 'ja' ? '24時間以内に返信' : 'Reply within 24 hours'}</span>
            </div>
          </div>

          <div className="p-8 md:p-10">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{t.bookingBlock.title}</h3>
              <p className="text-gray-500 text-base">{t.bookingBlock.subtitle}</p>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-start">
              {/* WhatsApp Side */}
              <div className="flex-1 flex flex-col items-center justify-center bg-white rounded-2xl p-8 border border-green-100 shadow-sm text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                  <i className="ri-whatsapp-line text-3xl text-green-500"></i>
                </div>
                <p className="text-gray-700 font-semibold text-lg mb-1">
                  {lang === 'ja' ? '即レス・日本語OK' : 'Instant Reply · English OK'}
                </p>
                <p className="text-gray-400 text-sm mb-6">
                  {lang === 'ja' ? '質問・日程調整・カスタムリクエストも気軽に' : 'Questions, scheduling, custom requests — all welcome'}
                </p>
                <button
                  onClick={handleWhatsAppClick}
                  className="inline-flex items-center gap-3 px-7 py-3.5 bg-green-500 hover:bg-green-600 text-white font-bold rounded-full transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer text-base"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  {t.bookingBlock.whatsappBtn}
                </button>
              </div>

              {/* Divider */}
              <div className="flex md:flex-col items-center gap-3 self-center">
                <div className="flex-1 md:w-px md:h-16 h-px w-16 bg-amber-200"></div>
                <span className="text-amber-500 font-bold text-sm bg-amber-100 px-3 py-1 rounded-full whitespace-nowrap">
                  {t.bookingBlock.orText}
                </span>
                <div className="flex-1 md:w-px md:h-16 h-px w-16 bg-amber-200"></div>
              </div>

              {/* Form Side */}
              <div className="flex-1 bg-white rounded-2xl p-8 border border-amber-100 shadow-sm">
                <h4 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                  <i className="ri-calendar-check-line text-amber-500"></i>
                  {t.bookingBlock.formTitle}
                </h4>

                {formSubmitted ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                      <i className="ri-check-line text-3xl text-green-500"></i>
                    </div>
                    <p className="text-gray-800 font-semibold text-lg">{t.bookingBlock.successMsg}</p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="izakaya-area-booking-form"
                    onSubmit={handleFormSubmit}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">{t.bookingBlock.fields.name}</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">{t.bookingBlock.fields.guests}</label>
                        <select
                          name="guests"
                          value={formData.guests}
                          onChange={e => setFormData(p => ({ ...p, guests: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 cursor-pointer"
                        >
                          {t.bookingBlock.guestOptions.map((opt, i) => (
                            <option key={i} value={String(i + 1)}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">{t.bookingBlock.fields.email}</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">{t.bookingBlock.fields.date}</label>
                      <input
                        type="date"
                        name="date"
                        required
                        value={formData.date}
                        onChange={e => setFormData(p => ({ ...p, date: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200 cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">{t.bookingBlock.fields.message}</label>
                      <textarea
                        name="message"
                        rows={3}
                        maxLength={500}
                        value={formData.message}
                        onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200 resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-all duration-300 shadow-md hover:shadow-lg text-sm whitespace-nowrap cursor-pointer"
                    >
                      {t.bookingBlock.fields.submit}
                    </button>
                    <p className="text-center text-xs text-gray-400">{t.bookingBlock.note}</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* ===== END BOOKING CTA BLOCK ===== */}

        {/* Access Info */}
        <div className="bg-amber-50 rounded-2xl p-6 flex items-start gap-4 mb-10 border border-amber-100">
          <div className="w-12 h-12 flex items-center justify-center bg-amber-200 rounded-xl flex-shrink-0">
            <i className={`${t.access.icon} text-2xl text-amber-700`}></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-1">{t.access.title}</h4>
            <p className="text-gray-600">{t.access.description}</p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={handleWhatsAppClick}
            className="inline-flex items-center gap-3 px-8 py-4 bg-green-500 hover:bg-green-600 text-white text-lg font-semibold rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-2xl"></i>
            {t.cta}
          </button>
        </div>
      </div>
    </section>
  );
};
